<ngx-spinner color="#cd0005"></ngx-spinner>

<!-- Applications View -->
<div *ngIf="loaded" id="applications-view" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
  <div class="mb-8">
    <h1 class="text-2xl font-bold text-gray-900">Applications</h1>
    <p class="mt-1 text-gray-600">
      Manage your connected applications and explore new integrations
    </p>
  </div>
  <!-- Tabs -->
  <div class="border-b border-gray-200 mb-8">
    <div class="flex space-x-8 overflow-x-auto">
      <button
        *ngFor="let tab of tabs"
        (click)="setTab(tab.key)"
        [ngClass]="{
          'border-primary text-primary': activeTab === tab.key,
          'border-transparent text-gray-500 hover:text-gray-700': activeTab !== tab.key,
        }"
        class="app-tab border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap"
      >
        {{ tab.label }}
        <span
          class="ml-1 px-2 inline-flex text-xs leading-5 rounded-full bg-gray-100 text-gray-800"
        >
          {{ appsFor(tab.key).length }}
        </span>
      </button>
    </div>
  </div>
  <div class="app-tab-panel space-y-6">
    <!-- Request by App ID, which reaches private applications the list does not show -->
    <form
      *ngIf="activeTab === 'available'"
      class="bg-white rounded-lg shadow p-6"
      (ngSubmit)="requestApplication()"
    >
      <div class="flex flex-col md:flex-row md:items-end gap-4">
        <div class="form-control w-full md:max-w-md">
          <label class="label" for="app-id">
            <span class="label-text">App ID</span>
          </label>
          <input
            id="app-id"
            name="appId"
            type="text"
            [(ngModel)]="appId"
            class="input w-full rounded border-gray-300 shadow-sm"
            placeholder="Enter the App ID you were given"
            autocomplete="off"
          />
        </div>
        <button
          type="submit"
          class="btn btn-secondary px-4 py-2 text-sm"
          [disabled]="!appId.trim() || requesting"
        >
          Request Access
        </button>
      </div>
      <p class="mt-2 text-sm text-gray-500">
        Private applications aren’t listed. Enter the App ID you were given to request access to
        one.
      </p>
    </form>
    <p *ngIf="activeTab === 'suspended'" class="text-sm text-gray-500">
      These applications’ developers have suspended your access. Your API keys are kept and work
      again once the developer resumes it.
    </p>
    <!-- App Card -->
    <div *ngFor="let app of appsFor(activeTab)" class="app-card bg-white rounded-lg shadow p-6">
      <div class="flex items-start justify-between">
        <div class="flex items-start space-x-4">
          <div
            class="w-18 h-18 shrink-0 bg-gray-100 rounded flex items-center justify-center overflow-hidden"
          >
            <ng-container *ngIf="app.data?.image_url; else defaultIcon">
              <img
                [ngSrc]="app.data.image_url"
                alt="App icon"
                class="w-full h-full object-contain"
                width="72"
                height="72"
              />
            </ng-container>
            <ng-template #defaultIcon>
              <i
                class="text-3xl text-gray-600"
                [ngClass]="app.enabled ? 'ri-flight-takeoff-line' : 'ri-bar-chart-box-line'"
              ></i>
            </ng-template>
          </div>
          <div>
            <h3 class="text-lg font-medium text-gray-900">{{ app.data.label }}</h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ app.data.description }}
            </p>
            <a
              [href]="app.data.url"
              target="_blank"
              class="text-primary hover:text-primary/80 text-sm mt-2 inline-flex items-center"
            >
              <i class="ri-external-link-line mr-1"></i>
              Visit Website
            </a>
            <div class="mt-3 flex flex-wrap gap-2">
              <span
                *ngFor="let perm of app.data.permissions | filterPermissions"
                class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
              >
                {{ perm }}
              </span>
            </div>
            <div class="mt-3 flex flex-wrap gap-2">
              <span
                *ngFor="let allowance of app.data | formatHubAllowances"
                class="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
              >
                {{ allowance }}
              </span>
            </div>
            <div
              class="mt-3 space-y-1 text-sm text-gray-700"
              *ngIf="app.data.ndc_token || app.data.hub_token"
            >
              <div *ngIf="app.data.ndc_token">
                <strong class="text-gray-900">AGW API Key:&nbsp;</strong>
                <span class="break-all">{{ app.data.ndc_token }}</span>
              </div>
              <div *ngIf="app.data.hub_token">
                <strong class="text-gray-900">Hub Token:&nbsp;</strong>
                <span class="break-all">{{ app.data.hub_token }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col items-end space-y-2 shrink-0">
          <button
            *ngIf="!app.enabled"
            class="btn btn-secondary px-4 py-2 text-sm"
            (click)="openConfirmationModal(app)"
          >
            Request Access
          </button>
          <button
            *ngIf="app.enabled && canRevoke(app)"
            class="btn btn-primary px-4 py-2 text-sm"
            (click)="openConfirmationModal(app)"
          >
            {{ revokeLabel(app) }}
          </button>
        </div>
      </div>
    </div>
    <div *ngIf="!appsFor(activeTab).length" class="py-4 text-gray-500">
      {{ activeTabEmpty }}
    </div>
  </div>
</div>

<input type="checkbox" id="confirm-modal" class="modal-toggle" [checked]="modalVisible" hidden />
<div class="modal" [ngClass]="{ 'modal-open': modalVisible }" (click)="closeModal()">
  <div class="modal-box">
    <h3 class="font-bold text-lg">Confirm Action</h3>
    <p class="py-4">
      {{ confirmationMessage }}
    </p>
    <div class="modal-action" *ngIf="modalVisible">
      <button class="btn btn-outline" (click)="closeModal()">Cancel</button>
      <button
        class="btn"
        [ngClass]="{
          'btn-primary': selectedApp?.enabled,
          'btn-secondary': !selectedApp?.enabled,
        }"
        (click)="confirmToggle()"
      >
        {{ selectedApp?.enabled ? revokeLabel(selectedApp!) : 'Request Access' }}
      </button>
    </div>
  </div>
</div>
